import styled from '@emotion/styled';
import shouldForwardProp from '@styled-system/should-forward-prop';
import {
  background,
  BackgroundProps,
  border,
  BorderProps,
  color,
  compose,
  display,
  DisplayProps,
  flexbox,
  FlexboxProps,
  grid,
  GridProps,
  layout,
  LayoutProps,
  position,
  PositionProps,
  shadow,
  ShadowProps,
  space,
  SpaceProps,
  typography,
  TypographyProps,
} from 'styled-system';
import {
  ColorProps,
  CoreBaseProps,
  defineWithTheme,
  WithoutCollidingProps,
} from '../../theming';

export interface BaseTableProps
  extends CoreBaseProps,
    BackgroundProps,
    BorderProps,
    ColorProps,
    DisplayProps,
    FlexboxProps,
    GridProps,
    LayoutProps,
    PositionProps,
    ShadowProps,
    SpaceProps,
    TypographyProps {
  overflowScrolling?: string;
  WebkitOverflowScrolling?: string;
}

type NonCollidingTableProps = WithoutCollidingProps<BaseTableProps, 'table'>;
type NonCollidingTableKeys = keyof NonCollidingTableProps;

type NonCollidingTdProps = WithoutCollidingProps<BaseTableProps, 'td'>;
type NonCollidingTdKeys = keyof NonCollidingTdProps;

type NonCollidingThProps = WithoutCollidingProps<BaseTableProps, 'th'>;
type NonCollidingThKeys = keyof NonCollidingThProps;

type NonCollidingTrProps = WithoutCollidingProps<BaseTableProps, 'tr'>;
type NonCollidingTrKeys = keyof NonCollidingTrProps;

export const Table = styled('table', {
  shouldForwardProp: (prop): prop is NonCollidingTableKeys =>
    shouldForwardProp(prop as NonCollidingTableKeys),
})<BaseTableProps>(
  defineWithTheme('tables'),
  compose(
    background,
    border,
    color,
    display,
    flexbox,
    grid,
    layout,
    position,
    shadow,
    space,
    typography
  )
);

export const Td = styled('td', {
  shouldForwardProp: (prop): prop is NonCollidingTdKeys =>
    shouldForwardProp(prop as NonCollidingTdKeys),
})<BaseTableProps>(
  defineWithTheme('tableCells'),
  compose(
    background,
    border,
    color,
    display,
    flexbox,
    grid,
    layout,
    position,
    shadow,
    space,
    typography
  )
);

export const Th = styled('th', {
  shouldForwardProp: (prop): prop is NonCollidingThKeys =>
    shouldForwardProp(prop as NonCollidingThKeys),
})<BaseTableProps>(
  defineWithTheme('tableHeadings'),
  compose(
    background,
    border,
    color,
    display,
    flexbox,
    grid,
    layout,
    position,
    shadow,
    space,
    typography
  )
);

export const Tr = styled('tr', {
  shouldForwardProp: (prop): prop is NonCollidingTrKeys =>
    shouldForwardProp(prop as NonCollidingTrKeys),
})<BaseTableProps>(
  defineWithTheme('tableRows'),
  compose(
    background,
    border,
    color,
    display,
    flexbox,
    grid,
    layout,
    position,
    shadow,
    space,
    typography
  )
);

export type TableProps = React.ComponentPropsWithoutRef<typeof Table>;
export type TdProps = React.ComponentPropsWithoutRef<typeof Td>;
export type ThProps = React.ComponentPropsWithoutRef<typeof Th>;
export type TrProps = React.ComponentPropsWithoutRef<typeof Tr>;
